import React, {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { Button, CircularProgress } from "@mui/material";
import { SearchRounded } from "@mui/icons-material";
import Modal from "Components/UI/Modal/Modal";
import { selectAccount } from "Redux/Auth/slice";
import { selectPermissions } from "Redux/Role/slice";
import { client } from "Service/axios";
import { getCookie, readObj } from "Service/storage";
import {
  COMPANY,
  ACCOUNTS_SYSTEM_PERMISSION,
} from "Shared/Constants/apiRequest";
import { UserPermissions } from "Shared/Types/fleetmanagementapi";
import { CommandScope } from "./types";
import { CommandPermissions, getCommandAccess } from "./commandPermissions";
import {
  CommandPermissionsContext,
  parseCommandPermissions,
  commandPermissionFingerprint,
} from "./CommandPermissionsContext";
import { setActiveCommandScope } from "./commandSession";
import { useCommandDrafts } from "./commandDrafts";
import { clearCommandCache } from "./commandData";
import styles from "./CommandLauncher.module.scss";

const CommandCenter = lazy(() => import("./CommandCenter"));
const CommandContext = createContext<{
  launch: () => void;
  open: boolean;
  available: boolean;
} | null>(null);

function ScopeCommand({
  scope,
  permissions,
  open,
  onClose,
  onSavingChange,
}: {
  permissions: CommandPermissions;
  scope: CommandScope;
  open: boolean;
  onClose: () => void;
  onSavingChange: (saving: boolean) => void;
}) {
  const access = useMemo(() => getCommandAccess(permissions), [permissions]);
  const draftState = useCommandDrafts(scope);
  return (
    <CommandPermissionsContext.Provider value={permissions}>
      <Suspense
        fallback={
          open ? (
            <Modal
              open
              onClose={onClose}
              fullWidth={false}
              sx={{ zIndex: "10000000 !important" }}
            >
              <CircularProgress
                size={20}
                aria-label="Loading command center"
              />{" "}
              Loading command center…
            </Modal>
          ) : null
        }
      >
        <CommandCenter
          scope={scope}
          open={open}
          onClose={onClose}
          onSavingChange={onSavingChange}
          {...access}
          {...draftState}
        />
      </Suspense>
    </CommandPermissionsContext.Provider>
  );
}

export function CommandCenterProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const account = useSelector(selectAccount) as
    | ({
        id?: string | number;
        company?: { id?: string | number } | string | number;
      } & Record<string, unknown>)
    | null;
  const permissions = useSelector(selectPermissions);
  const permissionRevision = commandPermissionFingerprint(permissions);
  const userId = account?.id == null ? "" : String(account.id);
  const authCompany =
    typeof account?.company === "object"
      ? account?.company?.id
      : account?.company;
  const identity = userId ? JSON.stringify([userId, authCompany ?? ""]) : "";
  const [resolved, setResolved] = useState<{
    identity: string;
    permissionRevision: string;
    scope: CommandScope;
    permissions: CommandPermissions;
  } | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const verified =
    resolved?.identity === identity &&
    resolved.permissionRevision === permissionRevision
      ? resolved
      : null;
  const scope = verified?.scope;
  const permissionKey = commandPermissionFingerprint(verified?.permissions);
  const sessionRevision = useRef(0);
  const savingRef = useRef(false);
  const onSavingChange = useCallback((saving: boolean) => {
    savingRef.current = saving;
  }, []);
  const openRef = useRef(open);
  openRef.current = open;
  const onClose = useCallback(() => {
    if (!savingRef.current) setOpen(false);
  }, []);
  const launch = useCallback(() => {
    if (userId) setOpen(true);
  }, [userId]);

  // Do not derive company from a potentially stale Redux company response after
  // a different user signs in. Only the current authenticated /company/ result
  // is allowed to activate cache reads or browser drafts.
  useLayoutEffect(() => {
    setActiveCommandScope(null);
    clearCommandCache();
    savingRef.current = false;
    setOpen(false);
    return () => {
      setActiveCommandScope(null);
      clearCommandCache();
    };
  }, [identity, permissionRevision]);

  useEffect(() => {
    setResolved(null);
    setError(null);
    if (!userId) return;
    const controller = new AbortController();
    const startedRevision = sessionRevision.current;
    const sameUser = () => {
      try {
        return (
          sessionRevision.current === startedRevision &&
          String(readObj("account")?.id ?? "") === userId &&
          !!getCookie("access")
        );
      } catch {
        return false;
      }
    };
    if (!sameUser()) return;
    Promise.all([
      client.get(COMPANY, { signal: controller.signal }),
      client.get<UserPermissions>(ACCOUNTS_SYSTEM_PERMISSION, {
        signal: controller.signal,
        params: { refreshQuery: Date.now().toString() },
      }),
    ])
      .then(([response, permissionResponse]) => {
        if (controller.signal.aborted || !sameUser()) return;
        const company = response.data;
        if (!company?.id)
          throw new Error("Company information is unavailable.");
        const companyId = String(company.id);
        if (authCompany != null && String(authCompany) !== companyId)
          throw new Error(
            "Your company session changed. Reload the page to continue.",
          );
        const next = {
          key: JSON.stringify([userId, companyId]),
          userId,
          companyId,
          companyName: company.account_name || "Current company",
        };
        const verifiedPermissions = parseCommandPermissions(
          permissionResponse.data,
        );
        setActiveCommandScope(next.key);
        setResolved({
          identity,
          permissionRevision,
          scope: next,
          permissions: verifiedPermissions,
        });
      })
      .catch((reason) => {
        if (!controller.signal.aborted)
          setError(
            reason?.response?.status === 403
              ? "Your account cannot access command-center information."
              : "Could not verify your company and permissions. Check your connection and try again.",
          );
      });
    return () => controller.abort();
  }, [identity, userId, authCompany, permissionRevision, retry]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== "account") return;
      if (event.key === "account" && event.newValue) {
        try {
          const next = JSON.parse(event.newValue);
          const company =
            typeof next.company === "object" ? next.company?.id : next.company;
          if (
            JSON.stringify([String(next.id ?? ""), company ?? ""]) === identity
          )
            return;
        } catch {
          /* Invalid session state must invalidate visible records. */
        }
      }
      // Sign-out/account changes in another tab must also remove visible results.
      sessionRevision.current += 1;
      setActiveCommandScope(null);
      clearCommandCache();
      setResolved(null);
      setOpen(false);
      setError(
        "Your session changed in another tab. Reload the page to continue.",
      );
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [identity]);

  useEffect(() => {
    const keys = (event: KeyboardEvent) => {
      if (!userId || event.isComposing) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!savingRef.current) setOpen((value) => !value);
      } else if (event.key === "Escape" && openRef.current) {
        // Expanded record pickers own their first Escape; their handler stops
        // propagation after collapsing the list so the underlying modal stays put.
        const target = event.target;
        if (
          target instanceof HTMLElement &&
          target.matches('[role="combobox"][aria-expanded="true"]')
        )
          return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!savingRef.current) setOpen(false);
      }
    };
    window.addEventListener("keydown", keys, true);
    return () => window.removeEventListener("keydown", keys, true);
  }, [userId]);

  const context = useMemo(
    () => ({ launch, open, available: !!userId }),
    [launch, open, userId],
  );
  return (
    <CommandContext.Provider value={context}>
      {children}
      {scope && verified ? (
        <ScopeCommand
          key={`${scope.key}:${permissionKey}`}
          permissions={verified.permissions}
          scope={scope}
          open={open}
          onClose={onClose}
          onSavingChange={onSavingChange}
        />
      ) : (
        !!userId && (
          <Modal
            open={open}
            onClose={onClose}
            title="Command center"
            fullWidth={false}
            sx={{ zIndex: "10000000 !important" }}
          >
            {error || (
              <span>
                <CircularProgress size={18} aria-label="Loading company" />{" "}
                Verifying your company and permissions…
              </span>
            )}
            <div className={styles.loadActions}>
              <Button onClick={onClose}>Close</Button>
              {error && (
                <Button onClick={() => setRetry((value) => value + 1)}>
                  Try again
                </Button>
              )}
            </div>
          </Modal>
        )
      )}
    </CommandContext.Provider>
  );
}

export function CommandLauncher() {
  const context = useContext(CommandContext);
  const mac = /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <button
      type="button"
      className={styles.launcher}
      onClick={context?.launch}
      disabled={!context?.available}
      aria-haspopup="dialog"
      aria-expanded={!!context?.open}
      aria-label="Open command center: search, navigate, or create"
    >
      <SearchRounded aria-hidden="true" />
      <span>Search or jump to…</span>
      <kbd>{mac ? "⌘ K" : "Ctrl K"}</kbd>
    </button>
  );
}
